import FullCalendar from "@fullcalendar/react";
import {
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  EventInput,
} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import {
  CalendarEvent,
  CalendarRecurrenceFrequency,
  CalendarRecurrenceResponse,
  CalendarSchedulePayload,
  CalendarView,
  createPsychologistCalendarRecurrence,
  createPsychologistCalendarSchedule,
  deletePsychologistCalendarSchedule,
  getPsychologistCalendar,
  updatePsychologistCalendarSchedule,
} from "src/apis/psychologist";
import {
  Collaborator,
  Company,
  filterVisibleCompanies,
  GetCompanies,
  searchCollaboratorsByCompany,
} from "src/apis/company";
import { getApiErrorMessage } from "src/apis/prontuario";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import { Id } from "src/utils/types";
import { useDebouncedCallback } from "use-debounce";

type ServicesCalendarProps = {
  psychologistId: Id;
};

type CalendarMode = "create" | "edit";

type RecurrenceOption = "none" | CalendarRecurrenceFrequency;

type CalendarFormValues = {
  company_id: string;
  collaborator_id: string;
  date: string;
  time: string;
  duration_minutes: string;
  notes: string;
  recurrence_frequency: RecurrenceOption;
};

type CurrentRange = {
  startDate: string;
  endDate: string;
  view: CalendarView;
};

const emptyForm: CalendarFormValues = {
  company_id: "",
  collaborator_id: "",
  date: "",
  time: "",
  duration_minutes: "50",
  notes: "",
  recurrence_frequency: "none",
};

const pad = (value: number): string => String(value).padStart(2, "0");

const formatLocalDate = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const formatLocalTime = (date: Date): string =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`;

const addDays = (date: Date, days: number): Date => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const toLocalIso = (date: string, time: string): string => `${date}T${time}:00`;

const getCalendarView = (viewType: string): CalendarView => {
  if (viewType.includes("Day")) return "day";
  if (viewType.includes("Week")) return "week";
  return "month";
};

const getRecordValue = (value: unknown, key: string): unknown =>
  value && typeof value === "object"
    ? (value as Record<string, unknown>)[key]
    : undefined;

const getScheduleFromPayload = (value: unknown): CalendarEvent => {
  const nestedSchedule =
    getRecordValue(value, "data") ??
    getRecordValue(value, "schedule") ??
    getRecordValue(value, "event") ??
    getRecordValue(value, "calendar_event") ??
    value;

  return (nestedSchedule ?? {}) as CalendarEvent;
};

const getScheduleIdFromPayload = (value: unknown): string => {
  const schedule = getScheduleFromPayload(value);

  return String(
    schedule.schedule_id ??
      schedule.id ??
      getRecordValue(value, "schedule_id") ??
      getRecordValue(value, "id") ??
      ""
  );
};

const getScheduleId = (event: CalendarEvent): string =>
  getScheduleIdFromPayload(event);

const getScheduleStart = (event: CalendarEvent): string =>
  String(
    event.scheduled_start_at ??
      event.start ??
      (event.date && event.time ? `${event.date}T${event.time}:00` : "")
  );

const getScheduleEnd = (event: CalendarEvent): string =>
  String(event.scheduled_end_at ?? event.end ?? "");

const getScheduleStatus = (event: CalendarEvent): string =>
  String(event.attendance_status ?? event.status ?? "Desconhecido");

const getStatusColor = (status: string): string => {
  const normalizedStatus = status.trim().toLowerCase();

  if (normalizedStatus === "feita" || normalizedStatus === "realizado") return "#2e7d32";
  if (normalizedStatus === "falta" || normalizedStatus === "paciente faltou") return "#d32f2f";
  return "#607d8b";
};

const getRecurrenceSuccessMessage = (
  baseMessage: string,
  recurrence: CalendarRecurrenceResponse
): string => {
  const skippedMessage =
    recurrence.skipped_count > 0
      ? " Alguns horários já existiam e foram ignorados."
      : "";

  return `${baseMessage} com ${recurrence.created_count} recorrências.${skippedMessage}`;
};

const getFallbackTitle = (event: CalendarEvent): string => {
  const psychologistName = event.psychologist_name ?? "Psicólogo";
  const collaboratorName = event.collaborator_name ?? "Paciente";
  const companyName = event.company_name ?? "Empresa";

  return `${psychologistName} x ${collaboratorName} + ${companyName}`;
};

const getDurationMinutes = (event: CalendarEvent): number => {
  const start = getScheduleStart(event);
  const end = getScheduleEnd(event);

  if (!start || !end) return 50;

  const startDate = new Date(start);
  const endDate = new Date(end);
  const diff = Math.round((endDate.getTime() - startDate.getTime()) / 60000);

  return Number.isFinite(diff) && diff > 0 ? diff : 50;
};

const makeCollaboratorFromEvent = (event: CalendarEvent): Collaborator | null => {
  if (!event.collaborator_id) return null;

  return {
    id_colaborador: Number(event.collaborator_id),
    id_empresa: Number(event.company_id ?? 0),
    nome: String(event.collaborator_name ?? ""),
    setor: "",
    cargo: "",
    unidade: "",
    observacoes: "",
  };
};

const ServicesCalendar: FC<ServicesCalendarProps> = ({ psychologistId }) => {
  const calendarRef = useRef<FullCalendar | null>(null);
  const [events, setEvents] = useState<EventInput[]>([]);
  const [currentRange, setCurrentRange] = useState<CurrentRange | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [modalError, setModalError] = useState<string>("");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<CalendarMode>("create");
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [formValues, setFormValues] = useState<CalendarFormValues>(emptyForm);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState<boolean>(false);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [selectedCollaborator, setSelectedCollaborator] =
    useState<Collaborator | null>(null);
  const [collaboratorSearch, setCollaboratorSearch] = useState<string>("");
  const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);
  const [collaboratorsLoading, setCollaboratorsLoading] =
    useState<boolean>(false);

  const statusLegend = useMemo(
    () => [
      { label: "Realizado", color: getStatusColor("Realizado") },
      { label: "Paciente Faltou", color: getStatusColor("Paciente Faltou") },
      { label: "Agendado", color: getStatusColor("Agendado") },
    ],
    []
  );

  const isSaveDisabled = useMemo(
    () =>
      actionLoading ||
      !formValues.company_id ||
      !formValues.collaborator_id ||
      !formValues.date ||
      !formValues.time,
    [actionLoading, formValues]
  );

  const mapCalendarEvent = useCallback((schedule: CalendarEvent): EventInput => {
    const status = getScheduleStatus(schedule);
    const color = getStatusColor(status);
    const title = schedule.title ?? getFallbackTitle(schedule);

    return {
      id: getScheduleId(schedule),
      title,
      start: getScheduleStart(schedule),
      end: getScheduleEnd(schedule) || undefined,
      backgroundColor: color,
      borderColor: color,
      textColor: "#fff",
      extendedProps: {
        schedule: {
          ...schedule,
          title,
          status,
        },
      },
    };
  }, []);

  const loadCalendar = useCallback(
    async (range: CurrentRange) => {
      if (!psychologistId) return;

      setLoading(true);
      try {
        const response = await getPsychologistCalendar(
          psychologistId,
          range.startDate,
          range.endDate,
          range.view
        );

        setEvents((response.events ?? []).map(mapCalendarEvent));
        setError("");
      } catch (err: unknown) {
        setError(getApiErrorMessage(err, "Não foi possível carregar os agendamentos."));
      } finally {
        setLoading(false);
      }
    },
    [mapCalendarEvent, psychologistId]
  );

  const reloadCalendar = useCallback(async (endDate?: string) => {
    if (!currentRange) return;
    await loadCalendar({
      ...currentRange,
      endDate: endDate ?? currentRange.endDate,
    });
  }, [currentRange, loadCalendar]);

  const loadCompanies = useCallback(async () => {
    if (companies.length > 0) return;

    setCompaniesLoading(true);
    try {
      const response = await GetCompanies();
      setCompanies(filterVisibleCompanies(response.data ?? []));
      setModalError("");
    } catch (err: unknown) {
      setModalError(getApiErrorMessage(err, "Não foi possível carregar empresas."));
    } finally {
      setCompaniesLoading(false);
    }
  }, [companies.length]);

  const loadCollaborators = useCallback(
    async (companyId: string, search = "") => {
      if (!companyId) {
        setCollaborators([]);
        return;
      }

      if (!search.trim()) {
        setCollaborators([]);
        return;
      }

      setCollaboratorsLoading(true);
      try {
        const response = await searchCollaboratorsByCompany(companyId, search, true);
        setCollaborators(response);
        setModalError("");
      } catch (err: unknown) {
        setModalError(getApiErrorMessage(err, "Não foi possível carregar colaboradores."));
      } finally {
        setCollaboratorsLoading(false);
      }
    },
    []
  );

  const debouncedLoadCollaborators = useDebouncedCallback(
    (companyId: string, search: string) => {
      loadCollaborators(companyId, search);
    },
    400
  );

  const openCreateModal = (date: Date, allDay = false) => {
    const time = allDay ? "09:00" : formatLocalTime(date);

    setModalMode("create");
    setSelectedEvent(null);
    setSelectedCollaborator(null);
    setCollaboratorSearch("");
    setCollaborators([]);
    setFormValues({
      ...emptyForm,
      date: formatLocalDate(date),
      time,
    });
    setModalError("");
    setSuccessMessage("");
    setModalOpen(true);
    loadCompanies();
  };

  const openEditModal = (schedule: CalendarEvent) => {
    const start = getScheduleStart(schedule);
    const startDate = start ? new Date(start) : new Date();
    const collaborator = makeCollaboratorFromEvent(schedule);
    const companyId = String(schedule.company_id ?? "");

    setModalMode("edit");
    setSelectedEvent(schedule);
    setSelectedCollaborator(collaborator);
    setCollaboratorSearch(collaborator?.nome ?? "");
    setCollaborators(collaborator ? [collaborator] : []);
    setFormValues({
      company_id: companyId,
      collaborator_id: String(schedule.collaborator_id ?? ""),
      date: formatLocalDate(startDate),
      time: formatLocalTime(startDate),
      duration_minutes: String(getDurationMinutes(schedule)),
      notes: String(schedule.notes ?? schedule.observacoes ?? ""),
      recurrence_frequency: "none",
    });
    setModalError("");
    setSuccessMessage("");
    setModalOpen(true);
    loadCompanies();
    if (companyId) {
      loadCollaborators(companyId, String(schedule.collaborator_name ?? ""));
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalError("");
    setSelectedEvent(null);
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    const range = {
      startDate: formatLocalDate(arg.start),
      endDate: formatLocalDate(addDays(arg.end, -1)),
      view: getCalendarView(arg.view.type),
    };

    setCurrentRange(range);
    loadCalendar(range);
  };

  const handleDateClick = (arg: DateClickArg) => {
    openCreateModal(arg.date, arg.allDay);
  };

  const handleEventClick = (arg: EventClickArg) => {
    arg.jsEvent.preventDefault();
    arg.jsEvent.stopPropagation();

    openEditModal(arg.event.extendedProps.schedule as CalendarEvent);
  };

  const buildPayload = (): CalendarSchedulePayload => ({
    company_id: Number(formValues.company_id),
    collaborator_id: Number(formValues.collaborator_id),
    scheduled_start_at: toLocalIso(formValues.date, formValues.time),
    duration_minutes: Number(formValues.duration_minutes) || 50,
    notes: formValues.notes,
    date: formValues.date,
    time: formValues.time,
  });

  const handleSave = async () => {
    if (isSaveDisabled) {
      setModalError("Preencha empresa, colaborador, data e hora para salvar.");
      return;
    }

    setActionLoading(true);
    try {
      const payload = buildPayload();

      if (modalMode === "edit" && selectedEvent) {
        await updatePsychologistCalendarSchedule(
          psychologistId,
          getScheduleId(selectedEvent),
          payload
        );

        let recurrenceUntil: string | undefined;

        if (formValues.recurrence_frequency !== "none") {
          try {
            const recurrence = await createPsychologistCalendarRecurrence(
              psychologistId,
              getScheduleId(selectedEvent),
              {
                frequency: formValues.recurrence_frequency,
                months_ahead: 2,
              }
            );

            recurrenceUntil = recurrence.until?.split("T")[0];
            setSuccessMessage(
              getRecurrenceSuccessMessage("Agendamento atualizado", recurrence)
            );
          } catch (err: unknown) {
            closeModal();
            await reloadCalendar();
            setError(
              getApiErrorMessage(
                err,
                "Agendamento atualizado, mas não foi possível criar a recorrência."
              )
            );
            return;
          }
        } else {
          setSuccessMessage("Agendamento atualizado.");
        }

        closeModal();
        await reloadCalendar(recurrenceUntil);
        return;
      } else {
        const createdSchedule = await createPsychologistCalendarSchedule(
          psychologistId,
          payload
        );
        let recurrenceUntil: string | undefined;

        if (formValues.recurrence_frequency !== "none") {
          const scheduleId = getScheduleIdFromPayload(createdSchedule);

          if (!scheduleId) {
            throw new Error("Não foi possível identificar o agendamento criado.");
          }

          try {
            const recurrence = await createPsychologistCalendarRecurrence(
              psychologistId,
              scheduleId,
              {
                frequency: formValues.recurrence_frequency,
                months_ahead: 2,
              }
            );

            recurrenceUntil = recurrence.until?.split("T")[0];
            setSuccessMessage(
              getRecurrenceSuccessMessage("Agendamento criado", recurrence)
            );
          } catch (err: unknown) {
            closeModal();
            await reloadCalendar();
            setError(
              getApiErrorMessage(
                err,
                "Agendamento criado, mas não foi possível criar a recorrência."
              )
            );
            return;
          }
        } else {
          setSuccessMessage("Agendamento criado.");
        }

        closeModal();
        await reloadCalendar(recurrenceUntil);
        return;
      }
    } catch (err: unknown) {
      setModalError(getApiErrorMessage(err, "Não foi possível salvar o agendamento."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;

    if (!window.confirm("Deseja cancelar este agendamento?")) {
      return;
    }

    setActionLoading(true);
    try {
      await deletePsychologistCalendarSchedule(
        psychologistId,
        getScheduleId(selectedEvent)
      );
      closeModal();
      await reloadCalendar();
    } catch (err: unknown) {
      setModalError(getApiErrorMessage(err, "Não foi possível cancelar o agendamento."));
    } finally {
      setActionLoading(false);
    }
  };

  const renderEventContent = (content: EventContentArg) => {
    const schedule = content.event.extendedProps.schedule as CalendarEvent;
    const status = String(schedule.status ?? "Desconhecido");
    const timeText = content.timeText || formatLocalTime(content.event.start ?? new Date());

    return (
      <Box sx={{ lineHeight: 1.2, minWidth: 0, overflow: "hidden" }}>
        <Box sx={{ display: "flex", gap: 0.5, minWidth: 0, alignItems: "baseline" }}>
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
            {timeText}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {content.event.title}
          </Typography>
        </Box>
        <Typography
          component="div"
          sx={{
            fontSize: 10,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            opacity: 0.92,
          }}
        >
          {status}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        mt: 2,
        position: "relative",
        "& .fc": {
          fontFamily: "inherit",
          color: "#202124",
        },
        "& .fc .fc-toolbar.fc-header-toolbar": {
          alignItems: { xs: "stretch", md: "center" },
          borderBottom: "1px solid #dadce0",
          flexDirection: { xs: "column", md: "row" },
          gap: 1,
          mb: 0,
          p: { xs: 1.5, md: 2 },
        },
        "& .fc-toolbar-chunk": {
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          justifyContent: { xs: "center", md: "initial" },
        },
        "& .fc-toolbar-chunk:nth-of-type(2)": {
          order: { xs: -1, md: 0 },
        },
        "& .fc-toolbar-title": {
          color: "#202124",
          fontSize: { xs: "1.15rem", sm: "1.35rem", md: "1.55rem" },
          fontWeight: 700,
          textTransform: "capitalize",
        },
        "& .fc-button-primary": {
          backgroundColor: "#fff",
          borderColor: "#dadce0",
          borderRadius: "8px",
          boxShadow: "none !important",
          color: "#3c4043",
          fontSize: { xs: "0.78rem", sm: "0.875rem" },
          fontWeight: 700,
          minHeight: 36,
          padding: { xs: "5px 8px", sm: "6px 12px" },
          textTransform: "none",
          "&:disabled": {
            backgroundColor: "#fff",
            borderColor: "#dadce0",
            color: "#9aa0a6",
          },
          "&:hover": {
            backgroundColor: "#f1f3f4",
            borderColor: "#dadce0",
            color: "#202124",
          },
        },
        "& .fc-button-primary:not(:disabled).fc-button-active, & .fc-button-primary:not(:disabled):active": {
          backgroundColor: "#e8f0fe",
          borderColor: "#1a73e8",
          color: "#1967d2",
        },
        "& .fc-newSchedule-button": {
          backgroundColor: "#1a73e8 !important",
          borderColor: "#1a73e8 !important",
          borderRadius: "8px !important",
          color: "#fff !important",
          paddingLeft: "14px !important",
          paddingRight: "14px !important",
          "&:hover": {
            backgroundColor: "#1558b0 !important",
            borderColor: "#1558b0 !important",
          },
        },
        "& .fc-scrollgrid": {
          border: "0 !important",
        },
        "& .fc-theme-standard td, & .fc-theme-standard th": {
          borderColor: "#e0e3e7",
        },
        "& .fc-col-header-cell": {
          backgroundColor: "#fff",
        },
        "& .fc-col-header-cell-cushion": {
          color: "#5f6368",
          fontSize: 12,
          fontWeight: 700,
          padding: "10px 4px",
          textTransform: "capitalize",
        },
        "& .fc-daygrid-day-number": {
          alignItems: "center",
          borderRadius: "50%",
          color: "#3c4043",
          display: "inline-flex",
          fontSize: 12,
          height: 28,
          justifyContent: "center",
          margin: "4px",
          width: 28,
        },
        "& .fc-day-today": {
          backgroundColor: "#f8fbff !important",
        },
        "& .fc-day-today .fc-daygrid-day-number": {
          backgroundColor: "#1a73e8",
          color: "#fff",
          fontWeight: 800,
        },
        "& .fc-event": {
          borderRadius: "6px",
          boxShadow: "none",
          cursor: "pointer",
          marginInline: "2px",
          padding: "3px 5px",
        },
        "& .fc-daygrid-event": {
          whiteSpace: "normal",
        },
        "& .fc-more-link": {
          color: "#1967d2",
          fontSize: 12,
          fontWeight: 700,
        },
        "& .fc-timegrid-slot": {
          height: 46,
        },
        "& .fc-timegrid-axis-cushion, & .fc-timegrid-slot-label-cushion": {
          color: "#5f6368",
          fontSize: 12,
        },
        "& .fc-timegrid-now-indicator-line": {
          borderColor: "#ea4335",
        },
        "& .fc-timegrid-now-indicator-arrow": {
          borderColor: "#ea4335",
        },
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            pointerEvents: "none",
            backgroundColor: "rgba(255,255,255,0.72)",
            p: 2,
          }}
        >
          <SkeletonLoad variant="cards" rows={2} />
        </Box>
      )}

      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #dadce0",
          borderRadius: 2,
          boxShadow: "0 1px 2px rgba(60, 64, 67, 0.08)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            borderBottom: "1px solid #edf0f3",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "space-between",
            px: { xs: 1.5, md: 2 },
            py: 1.25,
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight={700}>
            Agenda de atendimentos
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {statusLegend.map((item) => (
              <Box
                key={item.label}
                sx={{
                  alignItems: "center",
                  display: "inline-flex",
                  gap: 0.75,
                  color: "#5f6368",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                <Box
                  sx={{
                    bgcolor: item.color,
                    borderRadius: "50%",
                    height: 8,
                    width: 8,
                  }}
                />
                {item.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: "100%" }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: "today prev,next",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay newSchedule",
              }}
              customButtons={{
                newSchedule: {
                  text: "Novo agendamento",
                  click: () => openCreateModal(new Date(), true),
                },
              }}
              buttonText={{
                today: "Hoje",
                month: "Mês",
                week: "Semana",
                day: "Dia",
              }}
              locale={ptBrLocale}
              timeZone="local"
              allDaySlot={false}
              nowIndicator
              selectable
              selectMirror
              navLinks
              expandRows
              stickyHeaderDates
              firstDay={1}
              dayMaxEventRows={3}
              moreLinkClick="popover"
              eventDisplay="block"
              events={events}
              datesSet={handleDatesSet}
              eventClick={handleEventClick}
              eventContent={renderEventContent}
              dateClick={handleDateClick}
              height="auto"
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
              slotDuration="00:30:00"
              slotLabelFormat={{
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }}
              eventTimeFormat={{
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }}
            />
          </Box>
        </Box>
      </Box>

      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitle>
          {modalMode === "edit" ? "Editar agendamento" : "Novo agendamento"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {modalError && <Alert severity="error">{modalError}</Alert>}

            <FormControl fullWidth required disabled={companiesLoading || actionLoading}>
              <InputLabel id="schedule-company-select">Empresa</InputLabel>
              <Select
                labelId="schedule-company-select"
                label="Empresa"
                value={formValues.company_id}
                onChange={(event) => {
                  const companyId = String(event.target.value);
                  setFormValues((previousValues) => ({
                    ...previousValues,
                    company_id: companyId,
                    collaborator_id: "",
                  }));
                  setSelectedCollaborator(null);
                  setCollaboratorSearch("");
                  setCollaborators([]);
                  loadCollaborators(companyId, "");
                }}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={String(company.id)}>
                    {company.trade_name}
                  </MenuItem>
                ))}
              </Select>
              {companiesLoading && <FormHelperText>Carregando empresas...</FormHelperText>}
            </FormControl>

            <Autocomplete
              open={autocompleteOpen}
              onOpen={() => {
                if (formValues.company_id && collaboratorSearch.trim()) {
                  setAutocompleteOpen(true);
                }
              }}
              onClose={() => setAutocompleteOpen(false)}
              value={selectedCollaborator}
              inputValue={collaboratorSearch}
              disabled={!formValues.company_id || actionLoading}
              loading={collaboratorsLoading}
              options={collaborators}
              isOptionEqualToValue={(option, value) =>
                option.id_colaborador === value.id_colaborador
              }
              getOptionLabel={(option) => option.nome}
              filterOptions={(options) => options}
              noOptionsText={
                formValues.company_id
                  ? collaboratorSearch.trim()
                    ? "Nenhuma sugestão encontrada"
                    : "Digite o nome para buscar"
                  : "Selecione a empresa primeiro"
              }
              onInputChange={(_, newInputValue, reason) => {
                setCollaboratorSearch(newInputValue);

                if (reason !== "reset" && formValues.company_id) {
                  if (newInputValue.trim()) {
                    debouncedLoadCollaborators(formValues.company_id, newInputValue);
                    setAutocompleteOpen(true);
                  } else {
                    setCollaborators([]);
                    setAutocompleteOpen(false);
                  }
                }
              }}
              onChange={(_, collaborator) => {
                setSelectedCollaborator(collaborator);
                setFormValues((previousValues) => ({
                  ...previousValues,
                  collaborator_id: collaborator
                    ? String(collaborator.id_colaborador)
                    : "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Colaborador/Paciente"
                  helperText="Busque pelo nome do colaborador"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {collaboratorsLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                required
                label="Data"
                type="date"
                value={formValues.date}
                onChange={(event) =>
                  setFormValues((previousValues) => ({
                    ...previousValues,
                    date: event.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                disabled={actionLoading}
              />
              <TextField
                required
                label="Hora inicial"
                type="time"
                value={formValues.time}
                onChange={(event) =>
                  setFormValues((previousValues) => ({
                    ...previousValues,
                    time: event.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                disabled={actionLoading}
              />
            </Box>

            <TextField
              label="Duração em minutos"
              type="number"
              value={formValues.duration_minutes}
              onChange={(event) =>
                setFormValues((previousValues) => ({
                  ...previousValues,
                  duration_minutes: event.target.value,
                }))
              }
              inputProps={{ min: 1 }}
              disabled={actionLoading}
            />

            <FormControl fullWidth disabled={actionLoading}>
              <InputLabel id="schedule-recurrence-select">
                Essa sessão se repete?
              </InputLabel>
              <Select
                labelId="schedule-recurrence-select"
                label="Essa sessão se repete?"
                value={formValues.recurrence_frequency}
                onChange={(event) =>
                  setFormValues((previousValues) => ({
                    ...previousValues,
                    recurrence_frequency: event.target.value as RecurrenceOption,
                  }))
                }
              >
                <MenuItem value="none">Não repetir</MenuItem>
                <MenuItem value="weekly">Semanalmente</MenuItem>
                <MenuItem value="biweekly">Quinzenalmente</MenuItem>
              </Select>
              {formValues.recurrence_frequency !== "none" && (
                <FormHelperText>
                  Serão criadas sessões recorrentes para os próximos 2 meses.
                </FormHelperText>
              )}
            </FormControl>

            <TextField
              label="Observações"
              value={formValues.notes}
              onChange={(event) =>
                setFormValues((previousValues) => ({
                  ...previousValues,
                  notes: event.target.value,
                }))
              }
              minRows={3}
              multiline
              disabled={actionLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          <Box>
            {modalMode === "edit" && (
              <Button
                color="error"
                disabled={actionLoading}
                onClick={handleDelete}
              >
                Excluir/Cancelar agendamento
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button color="inherit" onClick={closeModal} disabled={actionLoading}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaveDisabled}
              startIcon={actionLoading ? <CircularProgress size={16} /> : undefined}
            >
              Salvar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicesCalendar;
